import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Organization } from '@/types/events';
import { fetchAcceptedMembershipOrgIds } from '@/hooks/useAcceptedMembershipOrgIds';
import { isMarketplaceVisibleEvent } from '@/utils/isMarketplaceVisibleEvent';
import { fetchEventPublicStats } from '@/utils/eventPublicStats';

const PAGE_SIZE = 15;

type EventRow = Record<string, unknown> & {
  id: string;
  name: string;
  location: string | null;
  status: string | null;
  image: string | null;
  start_date: string | null;
  end_date: string | null;
  departments: string[] | null;
  time_intervals: string[] | null;
  created_at: string;
  created_by: string;
  slots: number | null;
  is_public: boolean;
};

const BASE_SELECT =
  'id, name, image, location, start_date, end_date, status, slots, departments, time_intervals, is_public, created_by, created_at';

async function attachApplicationsAndMap(
  data: EventRow[],
  userId: string | null
): Promise<Organization[]> {
  if (!data || data.length === 0) return [];

  const eventIds = data.map((e) => e.id);
  // Counted server-side: RLS hides co-volunteers on events the viewer is not part of,
  // so counting the rows we can read would show 0 on every other org's event.
  const statsByEventId = await fetchEventPublicStats(eventIds);
  const { data: applicationsData } = userId
    ? await supabase
        .from('volunteer_applications')
        .select('event_id, id, status')
        .eq('volunteer_id', userId)
        .in('event_id', eventIds)
    : { data: null };

  const applicationByEventId: Record<string, { id: string; status: string }> = {};
  for (const row of applicationsData ?? []) {
    const r = row as { event_id: string; id: string; status: string };
    applicationByEventId[r.event_id] = { id: r.id, status: r.status };
  }

  const activeVolunteerEventIds = new Set<string>();
  if (userId && eventIds.length > 0) {
    const { data: volunteerRows } = await supabase
      .from('volunteers')
      .select('event_id')
      .eq('volunteer_id', userId)
      .eq('is_active', true)
      .in('event_id', eventIds);
    for (const row of volunteerRows ?? []) {
      activeVolunteerEventIds.add((row as { event_id: string }).event_id);
    }
  }

  return data.map((event) => {
    return {
      id: event.id,
      name: event.name,
      location: event.location ?? '',
      status: event.status ?? 'Active',
      image: event.image ?? null,
      start_date: event.start_date,
      end_date: event.end_date,
      departments: event.departments ?? [],
      time_intervals: event.time_intervals ?? [],
      created_at: event.created_at,
      created_by: event.created_by,
      slots: event.slots ?? 0,
      volunteerCount: statsByEventId[event.id]?.volunteerCount ?? 0,
      userApplication: applicationByEventId[event.id] ?? null,
      isAlreadyVolunteer: activeVolunteerEventIds.has(event.id),
      is_public: event.is_public,
    };
  }) as Organization[];
}

async function fetchPage(
  userId: string | null,
  from: number,
  limit: number
): Promise<Organization[]> {
  const { data, error } = await supabase
    .from('events')
    .select(BASE_SELECT)
    .eq('is_active', true)
    .eq('is_public', true)
    .order('created_at', { ascending: false })
    .range(from, from + limit - 1);
  if (error) throw error;

  return attachApplicationsAndMap(
    (data as EventRow[]).filter(isMarketplaceVisibleEvent),
    userId,
  );
}

/** Member-visible private events for orgs the volunteer is accepted into (RLS-enforced). */
async function fetchMemberPrivateEvents(userId: string | null): Promise<Organization[]> {
  if (!userId) return [];
  const orgIds = await fetchAcceptedMembershipOrgIds(userId);
  if (orgIds.length === 0) return [];

  const { data, error } = await supabase
    .from('events')
    .select(BASE_SELECT)
    .eq('is_active', true)
    .eq('is_public', false)
    .in('created_by', orgIds)
    .order('created_at', { ascending: false });
  if (error) throw error;

  return attachApplicationsAndMap(
    (data as EventRow[]).filter(isMarketplaceVisibleEvent),
    userId,
  );
}

function mergeDedupeSortByCreatedAt(a: Organization[], b: Organization[]): Organization[] {
  const byId = new Map<string, Organization>();
  for (const e of [...a, ...b]) {
    if (!byId.has(e.id)) byId.set(e.id, e);
  }
  return [...byId.values()].sort(
    (x, y) =>
      new Date(y.created_at ?? 0).getTime() - new Date(x.created_at ?? 0).getTime()
  );
}

export function usePublicEvents(userId: string | null) {
  const [events, setEvents] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  /** Offset into the public-only feed (excludes merged private rows). */
  const publicFeedOffsetRef = useRef(0);

  const fetchEvents = useCallback(async (forceRefresh = false) => {
    setLoading(true);
    if (forceRefresh) setError(null);
    try {
      const [publicPage, memberPrivate] = await Promise.all([
        fetchPage(userId, 0, PAGE_SIZE),
        fetchMemberPrivateEvents(userId),
      ]);
      publicFeedOffsetRef.current = publicPage.length;
      setEvents(mergeDedupeSortByCreatedAt(memberPrivate, publicPage));
      setHasMore(publicPage.length === PAGE_SIZE);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
      setEvents([]);
      setHasMore(false);
      publicFeedOffsetRef.current = 0;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const loadMore = useCallback(async () => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      const from = publicFeedOffsetRef.current;
      const page = await fetchPage(userId, from, PAGE_SIZE);
      publicFeedOffsetRef.current = from + page.length;
      setEvents((prev) => mergeDedupeSortByCreatedAt(prev, page));
      setHasMore(page.length === PAGE_SIZE);
    } catch {
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, hasMore, loading, loadingMore]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return {
    events,
    loading,
    error,
    hasMore,
    loadingMore,
    refetch: () => fetchEvents(true),
    loadMore,
  };
}
